import { useCallback, useState } from 'react'
import { useTimeoutFn } from 'react-use'

const DEFAULT_HIDE_DELAY_MS = 200

/**
 * Keeps an OverlayTrigger overlay visible while the user moves their mouse
 * from the trigger element into the overlay content, enabling interaction
 * with links or buttons inside the overlay.
 *
 * @example
 * const { overlayTriggerProps, hoverHandlers } = useInteractiveOverlay()
 *
 * <OverlayTrigger {...overlayTriggerProps} overlay={<Tooltip {...hoverHandlers}>content</Tooltip>}>
 *   <span {...hoverHandlers}>{trigger}</span>
 * </OverlayTrigger>
 */
export function useInteractiveOverlay(hideDelayMs = DEFAULT_HIDE_DELAY_MS) {
  const [show, setShow] = useState(false)
  const [, cancelHide, resetHide] = useTimeoutFn(() => setShow(false), hideDelayMs)

  const onMouseEnter = useCallback(() => {
    cancelHide()
    setShow(true)
  }, [cancelHide])

  const onMouseLeave = resetHide

  return {
    overlayTriggerProps: { show },
    hoverHandlers: { onMouseEnter, onMouseLeave },
    setShow,
  } as const
}
